// apps/admin/src/pages/setup/SetupWizardLayout.tsx
//
// Phase 1 Task 48 — Setup Wizard outer layout.
//
// Per CONTEXT (3-step wizard) + (X-Setup-Token gate).
// The wizard is rendered as a nested route tree:
//   /setup                → redirect /setup/create-admin (step 1)
//   /setup/create-admin   → SetupCreateAdminPage   (step 1)
//   /setup/enable-2fa     → SetupEnable2FAPage     (step 2)
//   /setup/recovery-codes → SetupRecoveryCodesPage (step 3)
//
// On first load this layout extracts `?token=…` from the URL and persists
// it to `sessionStorage` (auto-clears on tab close, NOT
// localStorage which would survive logout). All step pages read the token
// back from sessionStorage and attach it as `X-Setup-Token` to every
// `/api/v1/setup/*` POST.
//
// Token key: `setupToken` (storage key shared across the 3 step pages).

import { useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { Card, CardContent, cn } from '@printway/ui';

import { useTranslation } from '../../i18n/useTranslation.js';

export const SETUP_TOKEN_STORAGE_KEY = 'setupToken';

interface StepDef {
  index: 1 | 2 | 3;
  /** i18n key suffix under `setup.wizard.steps`. */
  labelKey: 'step1' | 'step2' | 'step3';
  /** Path segment under /setup. */
  segment: 'create' | '2fa' | 'recovery';
}

const STEPS: ReadonlyArray<StepDef> = [
  { index: 1, labelKey: 'step1', segment: 'create' },
  { index: 2, labelKey: 'step2', segment: '2fa' },
  { index: 3, labelKey: 'step3', segment: 'recovery' },
];

/**
 * Read token from URL `?token=…`, persist to sessionStorage, and strip the
 * query param so it doesn't linger in browser history.
 *
 * Returns the resolved token (URL-fresh OR previously persisted).
 */
function bootstrapSetupToken(
  search: string,
  navigate: ReturnType<typeof useNavigate>,
  pathname: string,
): string | null {
  if (typeof window === 'undefined') return null;

  // Pull from URL first.
  const params = new URLSearchParams(search);
  const fromUrl = params.get('token');
  if (fromUrl && fromUrl.length > 0) {
    try {
      window.sessionStorage.setItem(SETUP_TOKEN_STORAGE_KEY, fromUrl);
    } catch {
      // Storage might be disabled (private mode). Best-effort.
    }
    // Strip ?token=… from URL so a refresh / back-button doesn't leak.
    params.delete('token');
    const next = params.toString();
    navigate(
      { pathname, search: next.length > 0 ? `?${next}` : '' },
      { replace: true },
    );
    return fromUrl;
  }

  // Fall back to previously persisted token.
  try {
    return window.sessionStorage.getItem(SETUP_TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Determine which step is "active" based on current pathname. */
function activeStepIndex(pathname: string): 1 | 2 | 3 {
  if (pathname.endsWith('/enable-2fa')) return 2;
  if (pathname.endsWith('/recovery-codes')) return 3;
  return 1;
}

export default function SetupWizardLayout(): JSX.Element {
  const { t } = useTranslation('ops');
  const navigate = useNavigate();
  const location = useLocation();

  // First-load token bootstrap (runs once, idempotent — re-runs are no-op).
  useEffect(() => {
    bootstrapSetupToken(location.search, navigate, location.pathname);
    // Intentionally only on mount: subsequent steps must NOT re-extract.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const current = activeStepIndex(location.pathname);

  return (
    <main className="min-h-screen bg-pw-bg-muted px-4 py-10">
      <div className="mx-auto max-w-xl">
        <header className="mb-6 text-center">
          <h1 className="text-2xl font-semibold text-pw-text">
            {t('setup.wizard.title')}
          </h1>
          <p className="mt-1 text-sm text-pw-text-muted">
            {t('setup.wizard.subtitle')}
          </p>
        </header>

        {/* Stepper (1/2/3) */}
        <ol
          className="mb-6 flex items-center justify-between gap-2"
          aria-label={t('setup.wizard.progressAria')}
        >
          {STEPS.map((step) => {
            const isActive = step.index === current;
            const isDone = step.index < current;
            return (
              <li
                key={step.segment}
                className={cn(
                  'flex flex-1 flex-col items-center gap-2',
                  step.index < STEPS.length && 'border-r border-pw-border pr-2',
                )}
              >
                <span
                  className={cn(
                    'flex h-8 w-8 items-center justify-center rounded-full border text-sm font-semibold',
                    isActive &&
                      'border-pw-primary bg-pw-primary text-pw-primary-foreground',
                    isDone &&
                      'border-emerald-500 bg-emerald-500 text-white',
                    !isActive &&
                      !isDone &&
                      'border-pw-border bg-pw-bg text-pw-text-muted',
                  )}
                  aria-current={isActive ? 'step' : undefined}
                >
                  {step.index}
                </span>
                <span
                  className={cn(
                    'text-xs font-medium',
                    isActive ? 'text-pw-text' : 'text-pw-text-muted',
                  )}
                >
                  {t(`setup.wizard.steps.${step.labelKey}`)}
                </span>
              </li>
            );
          })}
        </ol>

        <Card>
          <CardContent className="p-6">
            <Outlet />
          </CardContent>
        </Card>
      </div>
    </main>
  );
}

/**
 * Read the persisted setup token (sessionStorage). Step pages call this
 * to populate the `X-Setup-Token` header on every `/api/v1/setup/*` call.
 */
export function readSetupToken(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage.getItem(SETUP_TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Clear the persisted setup token (call after wizard complete). */
export function clearSetupToken(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(SETUP_TOKEN_STORAGE_KEY);
  } catch {
    // ignore
  }
}
